import React from 'react';
import {observer} from 'mobx-react-lite';
import {useNavigate} from 'react-router-dom';
import {Calendar} from 'primereact/calendar';
import {Editor} from 'primereact/editor';
import {FileUpload} from 'primereact/fileupload';
import appStore from '../../../storage/AppStore';
import clubStore from '../../../storage/ClubStore';
import systemStore from '../../../storage/SystemStore';
import Input from '../../../ui/Input';
import Select from '../../../ui/Select';
import Btn from '../../../ui/Btn';
import ClickableMap from '../../../ui/ClickableMap';
import PageLayout from '../../PageLayout';
import CompetitionDivisions from '../../Games/CompetitionDivisions';
import {SAFE_FORMATS, SAFE_MODULES} from '../../../common/utils';
import 'quill/dist/quill.snow.css';
import '../../../common/game.css';

const Competition = () => {
  const navigate = useNavigate();
  const competition = clubStore.tournament.competition;

  return (
    <PageLayout header={
      <div className="competition-form__page-heading">
        <div className="competition-form__page-icon"><i className="pi pi-trophy" aria-hidden="true" /></div>
        <h3 className="competition-form__page-title">
          {competition.id > 0 ? competition.name : appStore.tr.add_tournament}
        </h3>
      </div>
    }>
      <div className="competition-form">
        <section className="competition-form__section">
          <div className="competition-form__section-heading">
            <span className="competition-form__section-icon"><i className="pi pi-info-circle" aria-hidden="true" /></span>
            <h4>{appStore.tr.competition || appStore.tr.tournament}</h4>
          </div>
          <div className="competition-form__grid">
            <div className="competition-form__field competition-form__field--wide">
              <label htmlFor="name">{appStore.tr.name}</label>
              <Input type="text" store={competition} prop="name" id="name" />
            </div>
            <div className="competition-form__field">
              <label htmlFor="sport">{appStore.tr.sport}</label>
              <Select store={competition} prop="sport_id" label={appStore.tr.sport}
                      options={clubStore.club.sport_names} id="sport" />
            </div>
            <div className="competition-form__field">
              <label htmlFor="address">{appStore.tr.address}</label>
              <Input type="text" store={competition} prop="address" id="address" />
            </div>
          </div>
        </section>

        <section className="competition-form__section">
          <div className="competition-form__section-heading">
            <span className="competition-form__section-icon"><i className="pi pi-calendar" aria-hidden="true" /></span>
            <h4>{appStore.tr.beginning} / {appStore.tr.end}</h4>
          </div>
          <div className="competition-form__grid">
            <div className="competition-form__field">
              <label htmlFor="beginning">{appStore.tr.beginning}</label>
              <Calendar className="p-inputtext-sm" value={competition.start_date}
                        maxDate={competition.end_date}
                        onChange={(e) => competition.setProperty('start_date', e.value)}
                        dateFormat="dd.mm.yy" id="beginning" />
            </div>
            <div className="competition-form__field">
              <label htmlFor="end">{appStore.tr.end}</label>
              <Calendar className="p-inputtext-sm" value={competition.end_date}
                        minDate={competition.start_date}
                        onChange={(e) => competition.setProperty('end_date', e.value)}
                        dateFormat="dd.mm.yy" id="end" />
            </div>
            <div className="competition-form__field">
              <label htmlFor="fee">{appStore.tr.fee}</label>
              <Input type="number" store={competition} prop="fee" id="fee" />
            </div>
          </div>
        </section>

        <section className="competition-form__section">
          <div className="competition-form__section-heading">
            <span className="competition-form__section-icon"><i className="pi pi-file-edit" aria-hidden="true" /></span>
            <h4>{appStore.tr.regulations}</h4>
          </div>
          <div className="competition-form__field">
            <label htmlFor="competition-regulations">{appStore.tr.regulations}</label>
            <div id="competition-regulations" className="competition-form__editor-wrapper">
              <Editor className="competition-form__editor" value={competition.regulations}
                      onTextChange={(e) => competition.setProperty('regulations', e.htmlValue)}
                      modules={SAFE_MODULES} formats={SAFE_FORMATS} />
            </div>
          </div>
          <div className="competition-form__file-row">
            {competition.pdf && (
              <a href={competition.pdf} target="_blank" rel="noopener noreferrer" className="competition-form__pdf-link">
                <Btn icon="pi pi-file-pdf" label={appStore.tr.regulations} severity="secondary" />
              </a>
            )}
            <div className="competition-form__upload">
              <label htmlFor="input_file[]">PDF: {appStore.tr.regulations}</label>
              <FileUpload mode="basic" name="input_file[]" accept="application/pdf"
                          chooseLabel={appStore.tr.file}
                          onSelect={(event) => {
                            competition.setProperty('pdf_temp', event.files[0]);
                          }} />
            </div>
          </div>
        </section>

        <section className="competition-form__section competition-form__section--divisions">
          <CompetitionDivisions tournamentId={competition.id} />
        </section>

        <section className="competition-form__section">
          <div className="competition-form__section-heading">
            <span className="competition-form__section-icon"><i className="pi pi-map-marker" aria-hidden="true" /></span>
            <h4>{appStore.tr.location}</h4>
          </div>
          <div className="competition-form__field competition-form__map">
            <label htmlFor="input_location">{appStore.tr.location}</label>
            <ClickableMap id="input_location" center={clubStore.club.location} zoom={12}
                          store={competition} prop="location" />
          </div>
        </section>

        <div className="competition-form__footer">
          <Btn icon="pi pi-save" severity="success" disabled={!competition.isValid}
               className="competition-form__save" label={appStore.tr.save}
               onClick={() => {
                 competition.save().then(() => {
                   systemStore.setSuccess(appStore.tr.successfully_saved);
                   clubStore.tournament.getCompetitions();
                   navigate('/manager/tournaments/list');
                 });
               }} />
        </div>
      </div>
    </PageLayout>
  );
};

export default observer(Competition);
